import { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import {
  getPhoneNumberError,
  PhoneNumberField,
  toE164,
} from "components/PhoneNumberField";
import {
  EQUIPMENT_VALUE_TO_LABEL_MAP,
  SERVICE_AREAS_TO_LABEL_MAP,
} from "constants/index";
import { useEditCompanyProfile } from "hooks";

import type { CompanyProfile } from "types/company";

interface Props {
  companyProfile: CompanyProfile;
  onModalClose: VoidFunction;
  modalOpen: boolean;
  onEditSuccess: VoidFunction;
}

interface CarrierProfileFormData {
  aboutUs: string;
  carrierType: BusinessTypeValue;
  truckCount: string;
  trailerCount: string;
  serviceAreas: string[];
  selectedEquipment: string[];
  phoneNumber: string;
  email: string;
  websiteUrl: string;
  facebookUrl: string;
  xUrl: string;
  linkedinUrl: string;
  instagramUrl: string;
}

interface FleetCountErrors {
  truckCount: string | null;
  trailerCount: string | null;
}

const NO_FLEET_COUNT_ERRORS: FleetCountErrors = {
  truckCount: null,
  trailerCount: null,
};

/**
 * Fleet counts are kept as strings so the inputs stay unrestricted, so reject
 * anything the backend would not accept as a count before converting: decimals
 * and appended text are otherwise silently truncated by `parseInt` ("2.5"
 * becomes 2), and negatives are rejected server-side.
 */
function getFleetCountError(value: string): string | null {
  if (!value) {
    return null;
  }
  return /^\d+$/.test(value) ? null : "Enter a whole number of 0 or more";
}

export function CarrierProfileEditModal(props: Props) {
  const { companyProfile, modalOpen, onModalClose, onEditSuccess } = props;

  const [formData, setFormData] = useState<CarrierProfileFormData>({
    aboutUs: "",
    carrierType: "",
    truckCount: "",
    trailerCount: "",
    serviceAreas: [],
    selectedEquipment: [],
    phoneNumber: "",
    email: "",
    websiteUrl: "",
    facebookUrl: "",
    xUrl: "",
    linkedinUrl: "",
    instagramUrl: "",
  });

  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [fleetCountErrors, setFleetCountErrors] = useState<FleetCountErrors>(
    NO_FLEET_COUNT_ERRORS,
  );

  // Closing on success only keeps a failed save on screen, where the dialog's
  // ErrorTypography can report it, instead of dismissing the dialog while the
  // request is still in flight and leaving the failure invisible.
  const { loading, error, editProfile, resetState } = useEditCompanyProfile({
    companyId: companyProfile.company.id,
    onEditSuccess: () => {
      onEditSuccess();
      onModalClose();
    },
  });

  useEffect(() => {
    if (companyProfile) {
      setFormData({
        aboutUs: companyProfile.company.description || "",
        carrierType: companyProfile.company.business_type ?? "",
        truckCount:
          companyProfile.profile_about?.number_of_trucks?.toString() ?? "",
        trailerCount:
          companyProfile.profile_about?.number_of_trailers?.toString() ?? "",
        serviceAreas: companyProfile.profile_about?.service_areas ?? [],
        selectedEquipment: companyProfile.profile_about?.equipment_types ?? [],
        phoneNumber: companyProfile.profile_about?.phone_number ?? "",
        email: companyProfile.profile_about?.email ?? "",
        websiteUrl: companyProfile.profile_about?.website_url ?? "",
        facebookUrl: companyProfile.profile_about?.facebook_url ?? "",
        xUrl: companyProfile.profile_about?.x_url ?? "",
        linkedinUrl: companyProfile.profile_about?.linkedin_url ?? "",
        instagramUrl: companyProfile.profile_about?.instagram_url ?? "",
      });
    }
  }, [companyProfile]);

  const handleFormChange = (
    field: keyof CarrierProfileFormData,
    value: string | string[],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleServiceAreaToggle = (area: string) => {
    const newServiceAreas = formData.serviceAreas.includes(area)
      ? formData.serviceAreas.filter((a) => a !== area)
      : [...formData.serviceAreas, area];
    handleFormChange("serviceAreas", newServiceAreas);
  };

  const handleEquipmentToggle = (equipment: string) => {
    const newEquipment = formData.selectedEquipment.includes(equipment)
      ? formData.selectedEquipment.filter((e) => e !== equipment)
      : [...formData.selectedEquipment, equipment];
    handleFormChange("selectedEquipment", newEquipment);
  };

  const handleClose = () => {
    resetState();
    setPhoneError(null);
    setFleetCountErrors(NO_FLEET_COUNT_ERRORS);
    onModalClose();
  };

  const handleSave = () => {
    // Phone is optional; validate only when provided so an incomplete or
    // malformed number is caught before submission instead of persisted.
    const phoneNumberError = getPhoneNumberError(formData.phoneNumber);
    if (phoneNumberError) {
      setPhoneError(phoneNumberError);
      return;
    }
    setPhoneError(null);

    // Only trucking companies own a fleet, so a carrier that switched to BROKER
    // clears the counts left behind in the form rather than persisting them.
    const isTruckingCompany = formData.carrierType === "TRUCKING_COMPANY";

    const nextFleetCountErrors = isTruckingCompany
      ? {
          truckCount: getFleetCountError(formData.truckCount),
          trailerCount: getFleetCountError(formData.trailerCount),
        }
      : NO_FLEET_COUNT_ERRORS;
    setFleetCountErrors(nextFleetCountErrors);
    if (nextFleetCountErrors.truckCount || nextFleetCountErrors.trailerCount) {
      return;
    }

    editProfile({
      description: formData.aboutUs,
      business_type: formData.carrierType,
      number_of_trucks:
        isTruckingCompany && formData.truckCount
          ? parseInt(formData.truckCount, 10)
          : null,
      number_of_trailers:
        isTruckingCompany && formData.trailerCount
          ? parseInt(formData.trailerCount, 10)
          : null,
      equipment_types: JSON.stringify(formData.selectedEquipment),
      service_areas: JSON.stringify(formData.serviceAreas),
      phone_number: formData.phoneNumber ? toE164(formData.phoneNumber) : null,
      email: formData.email || null,
      website_url: formData.websiteUrl || null,
      facebook_url: formData.facebookUrl || null,
      x_url: formData.xUrl || null,
      linkedin_url: formData.linkedinUrl || null,
      instagram_url: formData.instagramUrl || null,
    });
  };

  const equipmentTypes = Object.keys(EQUIPMENT_VALUE_TO_LABEL_MAP);
  const serviceAreaOptions = Object.keys(SERVICE_AREAS_TO_LABEL_MAP);

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle component="div">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" fontWeight="bold">
            Edit Carrier Profile
          </Typography>
          <Tooltip title="Close the profile editor without saving your changes">
            <IconButton
              aria-label="Close profile editor"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={3} sx={{ py: 2 }}>
          <Stack spacing={1}>
            <Typography variant="h6" fontWeight="bold">
              About Us
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder="Tell shippers about your company..."
              value={formData.aboutUs}
              onChange={(e) => handleFormChange("aboutUs", e.target.value)}
              inputProps={{ maxLength: 1024 }}
              helperText={`${formData.aboutUs.length}/1024 characters`}
            />
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">
              Carrier Type
            </Typography>
            <RadioGroup
              value={formData.carrierType}
              onChange={(e) => handleFormChange("carrierType", e.target.value)}
            >
              <FormControlLabel
                value="TRUCKING_COMPANY"
                control={<Radio />}
                label="Trucking Company"
              />
              <Stack spacing={1}>
                <Typography variant="body1" color="text.secondary">
                  We own and operate our own fleet of trucks and drivers. We
                  primarily handle freight transportation using our company
                  assets.
                </Typography>
                {formData.carrierType === "TRUCKING_COMPANY" && (
                  <Stack spacing={2} sx={{ pl: 4 }}>
                    <TextField
                      label="Number of Trucks"
                      type="text"
                      value={formData.truckCount}
                      onChange={(e) => {
                        handleFormChange("truckCount", e.target.value);
                        setFleetCountErrors((prev) => ({
                          ...prev,
                          truckCount: null,
                        }));
                      }}
                      error={fleetCountErrors.truckCount != null}
                      helperText={fleetCountErrors.truckCount ?? undefined}
                      fullWidth
                    />
                    <TextField
                      label="Number of Trailers"
                      type="text"
                      value={formData.trailerCount}
                      onChange={(e) => {
                        handleFormChange("trailerCount", e.target.value);
                        setFleetCountErrors((prev) => ({
                          ...prev,
                          trailerCount: null,
                        }));
                      }}
                      error={fleetCountErrors.trailerCount != null}
                      helperText={fleetCountErrors.trailerCount ?? undefined}
                      fullWidth
                    />
                  </Stack>
                )}
              </Stack>
              <FormControlLabel
                value="BROKER"
                control={<Radio />}
                label="Non-Asset Broker"
              />
              <Typography variant="body1" color="text.secondary">
                We coordinate freight transportation on behalf of shippers by
                partnering with outside carriers. We do not own or operate our
                own trucks.
              </Typography>
            </RadioGroup>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">
              Service Areas
            </Typography>
            <Stack spacing={1}>
              {serviceAreaOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={formData.serviceAreas.includes(option)}
                      onChange={() => handleServiceAreaToggle(option)}
                    />
                  }
                  label={SERVICE_AREAS_TO_LABEL_MAP[option]}
                />
              ))}
            </Stack>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">
              Equipment Types
            </Typography>
            <Grid container spacing={1}>
              {equipmentTypes.map((equipment) => (
                <Grid item xs={12} sm={6} md={4} key={equipment}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.selectedEquipment.includes(equipment)}
                        onChange={() => handleEquipmentToggle(equipment)}
                      />
                    }
                    label={EQUIPMENT_VALUE_TO_LABEL_MAP[equipment]}
                    sx={{ minWidth: "30%" }}
                  />
                </Grid>
              ))}
            </Grid>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">
              Emergency Contact Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Let shippers know how to reach you if there is an emergency with a
              load your company is moving for them. This information will be
              visible to shippers viewing your profile.
            </Typography>
            <Stack direction="row" spacing={2}>
              <PhoneNumberField
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(phoneNumber) => {
                  handleFormChange("phoneNumber", phoneNumber);
                  setPhoneError(null);
                }}
                error={phoneError != null}
                helperText={phoneError ?? undefined}
                fullWidth
              />
              <TextField
                label="Emergency Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange("email", e.target.value)}
                fullWidth
                placeholder="emergency@yourcompany.com"
              />
            </Stack>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">
              Website & Social Links
            </Typography>
            <TextField
              label="Website URL"
              type="url"
              value={formData.websiteUrl}
              onChange={(e) => handleFormChange("websiteUrl", e.target.value)}
              fullWidth
              placeholder="https://www.yourcompany.com"
            />
            <Stack spacing={2}>
              <TextField
                label="Facebook"
                type="url"
                value={formData.facebookUrl}
                onChange={(e) =>
                  handleFormChange("facebookUrl", e.target.value)
                }
                fullWidth
                placeholder="https://facebook.com/yourcompany"
              />
              <TextField
                label="X"
                type="url"
                value={formData.xUrl}
                onChange={(e) => handleFormChange("xUrl", e.target.value)}
                fullWidth
                placeholder="https://x.com/yourcompany"
              />
              <TextField
                label="LinkedIn"
                type="url"
                value={formData.linkedinUrl}
                onChange={(e) =>
                  handleFormChange("linkedinUrl", e.target.value)
                }
                fullWidth
                placeholder="https://linkedin.com/company/yourcompany"
              />
              <TextField
                label="Instagram"
                type="url"
                value={formData.instagramUrl}
                onChange={(e) =>
                  handleFormChange("instagramUrl", e.target.value)
                }
                fullWidth
                placeholder="https://instagram.com/yourcompany"
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <ErrorTypography error={error} />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <LoadingWrappedNode loading={loading}>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Save Changes
          </Button>
        </LoadingWrappedNode>
      </DialogActions>
    </Dialog>
  );
}
